import { useAuth, useSignIn, useSignUp } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { OAuthButton } from '@/components/common/auth/OAuthButton';
import { Logo } from '@/components/common/Logo';
import { SignUpForm } from '@/components/forms/auth/SignUpForm';
import { SignUpFormSchema } from '@/components/forms/schemas/SignUpFormSchema';
import { Button } from '@/components/ui/button';
import {
  OAUTH_PROVIDER_ORDER,
  OAUTH_PROVIDERS,
  OAuthProvider,
} from '@/constants/auth-providers';
import { toast } from '@/hooks/ui/use-toast';
import logger from '@/services/logger.service';

const ERROR_MESSAGES = {
  ACCEPTED_INVITATION_ERROR_MESSAGE:
    'This invitation has already been accepted. Sign in instead.',
  ALREADY_SIGNED_IN_ERROR_MESSAGE: "You're already signed in",
};

type SignUpFormValues = z.infer<typeof SignUpFormSchema>;

const AcceptOrgInvitation = ({
  isInternalHostname,
}: {
  isInternalHostname: boolean;
}) => {
  const navigate = useNavigate();
  const { orgId } = useParams();

  const [searchParams] = useSearchParams();

  const email = searchParams.get('email');
  const token = searchParams.get('__clerk_ticket');
  const accountStatus = searchParams.get('__clerk_status');
  const currentOrgId = orgId || window.localStorage.getItem('active-org-id');

  const { isSignedIn, signOut, isLoaded: isAuthLoaded } = useAuth();
  const {
    signIn,
    isLoaded: isLoadedSignIn,
    setActive: setActiveSignIn,
  } = useSignIn();
  const {
    signUp,
    isLoaded: isLoadedSignUp,
    setActive: setActiveSignUp,
  } = useSignUp();

  const [loading, setLoading] = useState(false);
  const [pendingProvider, setPendingProvider] = useState<OAuthProvider | null>(
    null
  );
  const [acceptedInvitation, setAcceptedInvitation] = useState<boolean>(false);
  const [rateLimitError, setRateLimitError] = useState<boolean>(false);

  useEffect(() => {
    if (
      !token ||
      accountStatus !== 'sign_in' ||
      !isLoadedSignIn ||
      !setActiveSignIn ||
      !isAuthLoaded ||
      rateLimitError
    ) {
      return;
    }
    const createSignIn = async () => {
      try {
        if (isSignedIn && signOut) {
          try {
            await signOut();
          } catch (err) {
            logger.error(
              'Failed to sign out before accepting invitation',
              err as Error
            );
          }
        }

        const signInAttempt = await signIn.create({
          strategy: 'ticket',
          ticket: token,
        });

        if (signInAttempt.status === 'complete') {
          if (orgId) {
            window.localStorage.setItem('active-org-id', orgId);
          }

          await setActiveSignIn({
            session: signInAttempt.createdSessionId,
          });

          navigate('/dashboard');
        } else {
          logger.error('Organization sign-in attempt failed', undefined, {
            signInAttempt,
            token,
          });
        }
      } catch (err) {
        if (
          err instanceof Error &&
          err.message &&
          err.message.includes(ERROR_MESSAGES.ACCEPTED_INVITATION_ERROR_MESSAGE)
        ) {
          setAcceptedInvitation(true);
          return;
        }

        if (
          err instanceof Error &&
          err.message &&
          err.message.includes(ERROR_MESSAGES.ALREADY_SIGNED_IN_ERROR_MESSAGE)
        ) {
          if (orgId) {
            window.localStorage.setItem('active-org-id', orgId);
          }
          navigate('/dashboard');
          return;
        }

        // Handle rate limit (429) errors
        if (
          err instanceof Error &&
          (err.message.includes('too_many_requests') ||
            err.message.includes('Too many requests'))
        ) {
          setRateLimitError(true);
          toast({
            variant: 'destructive',
            title: 'Too Many Requests',
            description: 'Too many requests. Please try again in a bit.',
          });
          return;
        }

        logger.error('Organization sign-in failed', err as Error, { token });
        Sentry.captureException(err as Error);
        setRateLimitError(true);
      }
    };

    createSignIn();
  }, [
    token,
    signIn,
    accountStatus,
    setActiveSignIn,
    rateLimitError,
    isSignedIn,
    signOut,
    isAuthLoaded,
    orgId,
    navigate,
  ]);

  const handleRetry = () => {
    setRateLimitError(false);
  };

  const handleSignUp = async (values: SignUpFormValues) => {
    if (!token || !isLoadedSignUp || !setActiveSignUp || !isAuthLoaded) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Sign up service is not available',
      });
      return;
    }

    try {
      if (isSignedIn && signOut) {
        try {
          await signOut();
        } catch (err) {
          logger.error(
            'Failed to sign out before accepting invitation',
            err as Error
          );
        }
      }

      const signUpAttempt = await signUp.create({
        strategy: 'ticket',
        ticket: token,
        emailAddress: values.email,
        password: values.password,
      });

      if (signUpAttempt.status === 'complete') {
        if (orgId) {
          window.localStorage.setItem('active-org-id', orgId);
        }

        await setActiveSignUp({ session: signUpAttempt.createdSessionId });

        toast({
          title: 'Success',
          description: 'Invitation accepted successfully',
        });
        navigate('/dashboard');
      } else {
        logger.error('Organization sign-up attempt failed', undefined, {
          signUpAttempt,
          token,
          email: values.email,
        });
        // toast({
        //   variant: 'destructive',
        //   title: 'Error',
        //   description: 'Failed to create account',
        // });
      }
    } catch (err) {
      if (
        err instanceof Error &&
        (err.message.includes('session_exists') ||
          err.message.includes('Session already exists') ||
          err.message.includes("You're already signed in"))
      ) {
        // Update localStorage with the new org ID
        if (orgId) {
          window.localStorage.setItem('active-org-id', orgId);
        }
        toast({
          title: 'Already Signed In',
          description: "You're already signed in. Redirecting to dashboard...",
        });
        navigate('/dashboard');
        return;
      }

      if (
        err instanceof Error &&
        (err.message.includes('too_many_requests') ||
          err.message.includes('Too many requests'))
      ) {
        setRateLimitError(true);
        toast({
          variant: 'destructive',
          title: 'Too Many Requests',
          description: 'Too many requests. Please try again in a bit.',
        });
        setLoading(false);
        return;
      }

      if (
        err instanceof Error &&
        err.message.includes('That email address is taken')
      ) {
        toast({
          title: 'Sign Up Failed',
          description: 'You already have an account. Please sign in.',
        });
        setLoading(false);
        navigate(`/${orgId ? orgId + '/' : ''}auth/sign-in`);
        return;
      }

      setLoading(false);
      logger.error('Organization sign-up failed', err as Error, {
        token,
        email: values.email,
      });
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create account',
      });
    }
  };

  const handleProviderSignUp = async (provider: OAuthProvider) => {
    if (!isLoadedSignUp || !signUp || !isAuthLoaded) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Sign up service is not available',
      });
      return;
    }

    setLoading(true);
    setPendingProvider(provider);
    try {
      if (isSignedIn && signOut) {
        try {
          await signOut();
        } catch (err) {
          logger.error(
            'Failed to sign out before accepting invitation',
            err as Error
          );
        }
      }

      if (!currentOrgId) {
        throw new Error('No active organization ID found');
      }

      window.localStorage.setItem('active-org-id', currentOrgId);

      // Start the invited sign-up first, then continue it with Google OAuth.
      // Without this, Clerk creates a plain app user and leaves the org
      // invitation unaccepted.
      await signUp.create({
        strategy: 'ticket',
        ticket: token ?? '',
      });

      await signUp.authenticateWithRedirect({
        strategy: OAUTH_PROVIDERS[provider].strategy,
        continueSignUp: true,
        redirectUrl: `${window.location.origin}/${currentOrgId}/auth/sso-callback?flow=invite`,
        redirectUrlComplete: `${window.location.origin}/dashboard`,
      });
    } catch (err) {
      logger.error('OAuth sign-up authentication failed', err as Error, {
        strategy: OAUTH_PROVIDERS[provider].strategy,
      });
      toast({
        variant: 'destructive',
        title: 'Something went wrong',
        description: 'Please try again or contact support',
      });
    } finally {
      setLoading(false);
      setPendingProvider(null);
    }
  };

  if (!token) {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-xl flex-col items-center justify-center gap-3 px-4 max-sm:items-center lg:w-1/2">
        <h2 className="as-strong text-black">No invitation token found.</h2>
      </div>
    );
  }

  if (accountStatus === 'sign_in' && !acceptedInvitation && !rateLimitError) {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-xl flex-col items-center justify-center gap-3 px-4 max-sm:items-center lg:w-1/2">
        <Loader2 className="size-4 animate-spin" />
        <h2 className="as-strong text-black">Joining the organization</h2>
      </div>
    );
  }

  if (acceptedInvitation && !rateLimitError) {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-[400px] flex-col justify-center px-4 max-sm:items-center lg:w-1/2">
        <div className="flex">{isInternalHostname && <Logo />}</div>
        <h2 className="as-strong mb-6 mt-10 text-center text-black">
          Organization invitation already accepted! Please sign in to continue
        </h2>
        <Button
          variant="default"
          className="w-full"
          onClick={() =>
            navigate(`/${currentOrgId ? currentOrgId + '/' : ''}auth/sign-in`)
          }
        >
          Sign in
        </Button>
      </div>
    );
  }

  if (rateLimitError) {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-[400px] flex-col justify-center px-4 max-sm:items-center lg:w-1/2">
        <div className="flex">{isInternalHostname && <Logo />}</div>
        <h2 className="as-strong mb-4 mt-10 text-center text-black">
          Too Many Requests
        </h2>
        <p className="as-body-02 mb-6 text-center text-neutral-600">
          We've received too many requests. Please wait try again after 10
          minutes.
        </p>
        <Button variant="default" className="w-full" onClick={handleRetry}>
          Try Again
        </Button>
      </div>
    );
  }

  if (accountStatus === 'sign_up') {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-[400px] flex-col justify-center px-4 max-sm:items-center lg:w-1/2">
        <div className="flex">{isInternalHostname && <Logo />}</div>
        <h2 className="as-strong mb-6 mt-10 text-black">Create your account</h2>
        <SignUpForm
          defaultEmail={email || undefined}
          customSubmit={handleSignUp}
        />
        <div className="my-4 flex items-center">
          <div className="h-[1px] w-full bg-neutral-200" />
          <p className="as-title-03 as-strong px-2 text-neutral-500">OR</p>
          <div className="h-[1px] w-full bg-neutral-200" />
        </div>
        <div className="space-y-3">
          {OAUTH_PROVIDER_ORDER.map((provider) => (
            <OAuthButton
              key={provider}
              provider={provider}
              loading={pendingProvider === provider}
              disabled={loading && pendingProvider !== provider}
              onClick={handleProviderSignUp}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-full w-full max-w-xl flex-col items-center justify-center gap-3 px-4 max-sm:items-center lg:w-1/2">
      <h2 className="as-strong text-black">
        Organization invitation accepted!
      </h2>
    </div>
  );
};

export default AcceptOrgInvitation;
